import { createVersionedContext, createVersionedValueMap, getOrCreateGlobalSingleton, useVersionedContext } from '@backstage/version-bridge';
import React, { useContext, useRef, useMemo, lazy, Suspense, useEffect, Children, isValidElement, Fragment } from 'react';
import { u as useApi, a as analyticsApiRef, f as featureFlagsApiRef } from './esm/StorageApi-c8a5e616.esm.js';
export { F as FeatureFlagState, S as SessionState, l as alertApiRef, a as analyticsApiRef, n as appThemeApiRef, k as atlassianAuthApiRef, i as bitbucketAuthApiRef, j as bitbucketServerAuthApiRef, p as configApiRef, c as createApiRef, q as discoveryApiRef, r as errorApiRef, f as featureFlagsApiRef, s as fetchApiRef, d as githubAuthApiRef, e as gitlabAuthApiRef, g as googleAuthApiRef, t as identityApiRef, m as microsoftAuthApiRef, x as oauthRequestApiRef, o as oktaAuthApiRef, h as oneloginAuthApiRef, y as storageApiRef, u as useApi, b as useApiHolder, v as vmwareCloudAuthApiRef, w as withApis } from './esm/StorageApi-c8a5e616.esm.js';
import { useLocation, useParams } from 'react-router-dom';
import '@backstage/errors';

const AnalyticsReactContext = createVersionedContext("analytics-context");
const useAnalyticsContext = () => {
  const theContext = useContext(AnalyticsReactContext);
  if (theContext === void 0) {
    return {
      routeRef: "unknown",
      pluginId: "root",
      extension: "App"
    };
  }
  const theValue = theContext.atVersion(1);
  if (theValue === void 0) {
    throw new Error("No context found for version 1.");
  }
  return theValue;
};
const AnalyticsContext = (options) => {
  const { attributes, children } = options;
  const parentValues = useAnalyticsContext();
  const combinedValue = {
    ...parentValues,
    ...attributes
  };
  const versionedCombinedValue = createVersionedValueMap({ 1: combinedValue });
  return /* @__PURE__ */ React.createElement(AnalyticsReactContext.Provider, { value: versionedCombinedValue }, children);
};

function createApiFactory(factory, instance) {
  if ("id" in factory) {
    return {
      api: factory,
      deps: {},
      factory: () => instance
    };
  }
  return factory;
}

const globalEvents = getOrCreateGlobalSingleton(
  "core-plugin-api:analytics-tracker-events",
  () => ({
    mostRecentGatheredNavigation: void 0,
    mostRecentRoutableExtensionRender: void 0,
    beforeUnloadRegistered: false
  })
);
const routableExtensionRenderedEvent = "_ROUTABLE-EXTENSION-RENDERED";
class Tracker {
  constructor(analyticsApi, context = {
    routeRef: "unknown",
    pluginId: "root",
    extension: "App"
  }) {
    this.analyticsApi = analyticsApi;
    this.context = context;
    if (!globalEvents.beforeUnloadRegistered) {
      addEventListener(
        "beforeunload",
        () => {
          if (globalEvents.mostRecentGatheredNavigation) {
            this.analyticsApi.captureEvent({
              ...globalEvents.mostRecentGatheredNavigation,
              ...globalEvents.mostRecentRoutableExtensionRender
            });
            globalEvents.mostRecentGatheredNavigation = void 0;
            globalEvents.mostRecentRoutableExtensionRender = void 0;
          }
        },
        { once: true, passive: true }
      );
      globalEvents.beforeUnloadRegistered = true;
    }
  }
  setContext(context) {
    this.context = context;
  }
  captureEvent(action, subject, {
    value,
    attributes
  } = {}) {
    const { _routeNodeType, ...context } = this.context;
    if (action === routableExtensionRenderedEvent) {
      if (globalEvents.mostRecentGatheredNavigation) {
        globalEvents.mostRecentRoutableExtensionRender = {
          context: {
            ...context,
            extension: "App"
          }
        };
      }
      return;
    }
    if (globalEvents.mostRecentGatheredNavigation) {
      try {
        this.analyticsApi.captureEvent({
          ...globalEvents.mostRecentGatheredNavigation,
          ...globalEvents.mostRecentRoutableExtensionRender
        });
      } catch (e) {
        console.warn("Error during analytics event capture. %o", e);
      }
      globalEvents.mostRecentGatheredNavigation = void 0;
      globalEvents.mostRecentRoutableExtensionRender = void 0;
    }
    if (action === "navigate" && _routeNodeType === "gathered" && context.pluginId === "root") {
      globalEvents.mostRecentGatheredNavigation = {
        action,
        subject,
        value,
        attributes,
        context
      };
      return;
    }
    try {
      this.analyticsApi.captureEvent({
        action,
        subject,
        value,
        attributes,
        context
      });
    } catch (e) {
      console.warn("Error during analytics event capture. %o", e);
    }
  }
}

function useAnalyticsApi() {
  try {
    return useApi(analyticsApiRef);
  } catch {
    return { captureEvent: () => {
    } };
  }
}
function useAnalytics() {
  const trackerRef = useRef(null);
  const context = useAnalyticsContext();
  const analyticsApi = useAnalyticsApi();
  function getTracker() {
    if (trackerRef.current === null) {
      trackerRef.current = new Tracker(analyticsApi);
    }
    return trackerRef.current;
  }
  const tracker = getTracker();
  tracker.setContext(context);
  return tracker;
}

const useApp = () => {
  const versionedContext = useVersionedContext(
    "app-context"
  );
  if (!versionedContext) {
    throw new Error("App context is not available");
  }
  const appContext = versionedContext.atVersion(1);
  if (!appContext) {
    throw new Error("AppContext v1 not available");
  }
  return appContext;
};

const globalStore = getOrCreateGlobalSingleton(
  "component-data-store",
  () => /* @__PURE__ */ new WeakMap()
);
const componentDataKey = "__backstage_data";
function attachComponentData(component, type, data) {
  var _a;
  const dataComponent = component;
  let container = (_a = dataComponent[componentDataKey]) != null ? _a : globalStore.get(component);
  if (!container) {
    container = { map: /* @__PURE__ */ new Map() };
    Object.defineProperty(dataComponent, componentDataKey, {
      enumerable: false,
      configurable: true,
      writable: false,
      value: container
    });
    globalStore.set(component, container);
  }
  if (container.map.has(type)) {
    const name = component.displayName || component.name;
    throw new Error(
      `Attempted to attach duplicate data "${type}" to component "${name}"`
    );
  }
  container.map.set(type, data);
}
function getComponentData(node, type) {
  var _a;
  if (!node) {
    return void 0;
  }
  const component = node.type;
  if (!component) {
    return void 0;
  }
  const container = (_a = component[componentDataKey]) != null ? _a : globalStore.get(component);
  if (!container) {
    return void 0;
  }
  return container.map.get(type);
}

const routeRefType = getOrCreateGlobalSingleton(
  "route-ref-type",
  () => Symbol("route-ref-type")
);

var __defProp$3 = Object.defineProperty;
var __defNormalProp$3 = (obj, key, value) => key in obj ? __defProp$3(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField$3 = (obj, key, value) => {
  __defNormalProp$3(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
var _a$2;
class RouteRefImpl {
  constructor(id, params) {
    this.id = id;
    this.params = params;
    __publicField$3(this, _a$2, "absolute");
  }
  get title() {
    return this.id;
  }
  toString() {
    return `routeRef{type=absolute,id=${this.id}}`;
  }
}
_a$2 = routeRefType;
function createRouteRef(config) {
  var _a2;
  return new RouteRefImpl(
    config.id,
    (_a2 = config.params) != null ? _a2 : []
  );
}

var __defProp$2 = Object.defineProperty;
var __defNormalProp$2 = (obj, key, value) => key in obj ? __defProp$2(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField$2 = (obj, key, value) => {
  __defNormalProp$2(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
var _a$1;
const PARAM_PATTERN = /^\w+$/;
class SubRouteRefImpl {
  constructor(id, path, parent, params) {
    this.id = id;
    this.path = path;
    this.parent = parent;
    this.params = params;
    __publicField$2(this, _a$1, "sub");
  }
  toString() {
    return `routeRef{type=sub,id=${this.id}}`;
  }
}
_a$1 = routeRefType;
function createSubRouteRef(config) {
  const { id, path, parent } = config;
  const pathParams = path.split("/").filter((p) => p.startsWith(":")).map((p) => p.substring(1));
  const params = [...parent.params, ...pathParams];
  if (parent.params.some((p) => pathParams.includes(p))) {
    throw new Error(
      "SubRouteRef may not have params that overlap with its parent"
    );
  }
  if (!path.startsWith("/")) {
    throw new Error(`SubRouteRef path must start with '/', got '${path}'`);
  }
  if (path.endsWith("/")) {
    throw new Error(`SubRouteRef path must not end with '/', got '${path}'`);
  }
  for (const param of pathParams) {
    if (!PARAM_PATTERN.test(param)) {
      throw new Error(`SubRouteRef path has invalid param, got '${param}'`);
    }
  }
  const subRouteRef = new SubRouteRefImpl(
    id,
    path,
    parent,
    params
  );
  return subRouteRef;
}

var __defProp$1 = Object.defineProperty;
var __defNormalProp$1 = (obj, key, value) => key in obj ? __defProp$1(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField$1 = (obj, key, value) => {
  __defNormalProp$1(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
var _a;
class ExternalRouteRefImpl {
  constructor(id, params, optional) {
    this.id = id;
    this.params = params;
    this.optional = optional;
    __publicField$1(this, _a, "external");
  }
  toString() {
    return `routeRef{type=external,id=${this.id}}`;
  }
}
_a = routeRefType;
function createExternalRouteRef(options) {
  var _a2;
  return new ExternalRouteRefImpl(
    options.id,
    (_a2 = options.params) != null ? _a2 : [],
    Boolean(options.optional)
  );
}

function useRouteRef(routeRef) {
  const { pathname } = useLocation();
  const versionedContext = useVersionedContext(
    "routing-context"
  );
  if (!versionedContext) {
    throw new Error("Routing context is not available");
  }
  const resolver = versionedContext.atVersion(1);
  const routeFunc = useMemo(
    () => resolver && resolver.resolve(routeRef, { pathname }),
    [resolver, routeRef, pathname]
  );
  if (!versionedContext) {
    throw new Error("useRouteRef used outside of routing context");
  }
  if (!resolver) {
    throw new Error("RoutingContext v1 not available");
  }
  const isOptional = "optional" in routeRef && routeRef.optional;
  if (!routeFunc && !isOptional) {
    throw new Error(`No path for ${routeRef}`);
  }
  return routeFunc;
}

function useRouteRefParams(_routeRef) {
  return useParams();
}

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
class PluginErrorBoundary extends React.Component {
  constructor() {
    super(...arguments);
    __publicField(this, "state", { error: void 0 });
    __publicField(this, "handleErrorReset", () => {
      this.setState({ error: void 0 });
    });
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    const { error } = this.state;
    const { app, plugin } = this.props;
    const { ErrorBoundaryFallback } = app.getComponents();
    if (error) {
      return /* @__PURE__ */ React.createElement(
        ErrorBoundaryFallback,
        {
          error,
          resetError: this.handleErrorReset,
          plugin
        }
      );
    }
    return this.props.children;
  }
}

function createRoutableExtension(options) {
  const { component, mountPoint, name } = options;
  return createReactExtension({
    component: {
      lazy: () => component().then(
        (InnerComponent) => {
          const RoutableExtensionWrapper = (props) => {
            const analytics = useAnalytics();
            try {
              useRouteRef(mountPoint);
            } catch (error) {
              if (typeof error === "object" && error !== null) {
                const { message } = error;
                if (typeof message === "string" && message.startsWith("No path for ")) {
                  throw new Error(
                    `Routable extension component with mount point ${mountPoint} was not discovered in the app element tree. Routable extension components may not be rendered by other components and must be directly available as an element within the App provider component.`
                  );
                }
              }
              throw error;
            }
            useEffect(() => {
              analytics.captureEvent(routableExtensionRenderedEvent, "");
            }, [analytics]);
            return /* @__PURE__ */ React.createElement(InnerComponent, { ...props });
          };
          const componentName = name || InnerComponent.displayName || InnerComponent.name || "LazyComponent";
          RoutableExtensionWrapper.displayName = `RoutableExtension(${componentName})`;
          return RoutableExtensionWrapper;
        },
        (error) => {
          const RoutableExtensionWrapper = (_) => {
            const app = useApp();
            const { BootErrorPage } = app.getComponents();
            return /* @__PURE__ */ React.createElement(BootErrorPage, { step: "load-chunk", error });
          };
          return RoutableExtensionWrapper;
        }
      )
    },
    data: {
      "core.mountPoint": mountPoint
    },
    name
  });
}
function createComponentExtension(options) {
  const { component, name } = options;
  return createReactExtension({ component, name });
}
function createReactExtension(options) {
  const { data = {}, name } = options;
  if (!name) {
    console.warn(
      "Declaring extensions without name is DEPRECATED. Make sure that all usages of createReactExtension, createComponentExtension and createRoutableExtension provide a name."
    );
  }
  let Component;
  if ("lazy" in options.component) {
    const lazyLoader = options.component.lazy;
    Component = lazy(
      () => lazyLoader().then((component) => ({ default: component }))
    );
  } else {
    Component = options.component.sync;
  }
  const componentName = name || Component.displayName || Component.name || "Component";
  return {
    expose(plugin) {
      const Result = (props) => {
        const app = useApp();
        const { Progress } = app.getComponents();
        const mountPoint = data == null ? void 0 : data["core.mountPoint"];
        return /* @__PURE__ */ React.createElement(Suspense, { fallback: /* @__PURE__ */ React.createElement(Progress, null) }, /* @__PURE__ */ React.createElement(PluginErrorBoundary, { app, plugin }, /* @__PURE__ */ React.createElement(
          AnalyticsContext,
          {
            attributes: {
              pluginId: plugin.getId(),
              ...name && { extension: name },
              ...mountPoint && { routeRef: mountPoint.id }
            }
          },
          /* @__PURE__ */ React.createElement(Component, { ...props })
        )));
      };
      attachComponentData(Result, "core.plugin", plugin);
      attachComponentData(Result, "core.extensionName", name);
      for (const [key, value] of Object.entries(data)) {
        attachComponentData(Result, key, value);
      }
      Result.displayName = `Extension(${componentName})`;
      return Result;
    }
  };
}

function selectChildren(rootNode, featureFlagsApi, selector, strictError) {
  return Children.toArray(rootNode).flatMap((node) => {
    if (!isValidElement(node)) {
      return [];
    }
    if (node.type === Fragment) {
      return selectChildren(
        node.props.children,
        featureFlagsApi,
        selector,
        strictError
      );
    }
    if (getComponentData(node, "core.featureFlagged")) {
      const props = node.props;
      const isEnabled = "with" in props ? featureFlagsApi.isActive(props.with) : !featureFlagsApi.isActive(props.without);
      if (isEnabled) {
        return selectChildren(
          node.props.children,
          featureFlagsApi,
          selector,
          strictError
        );
      }
      return [];
    }
    if (selector === void 0 || selector(node)) {
      return [node];
    }
    if (strictError) {
      throw new Error(strictError);
    }
    return selectChildren(
      node.props.children,
      featureFlagsApi,
      selector,
      strictError
    );
  });
}
class Collection {
  constructor(node, featureFlagsApi) {
    this.node = node;
    this.featureFlagsApi = featureFlagsApi;
  }
  selectByComponentData(query) {
    const selection = selectChildren(
      this.node,
      this.featureFlagsApi,
      (node) => getComponentData(node, query.key) !== void 0,
      query.withStrictError
    );
    return new Collection(selection, this.featureFlagsApi);
  }
  findComponentData(query) {
    const selection = selectChildren(
      this.node,
      this.featureFlagsApi,
      (node) => getComponentData(node, query.key) !== void 0
    );
    return selection.map((node) => getComponentData(node, query.key)).filter((data) => data !== void 0);
  }
  getElements() {
    return selectChildren(this.node, this.featureFlagsApi);
  }
}
function useElementFilter(node, filterFn, dependencies = []) {
  const featureFlagsApi = useApi(featureFlagsApiRef);
  const elements = new Collection(node, featureFlagsApi);
  return useMemo(() => filterFn(elements), [node, ...dependencies]);
}

class PluginImpl {
  constructor(config) {
    this.config = config;
  }
  getId() {
    return this.config.id;
  }
  getApis() {
    var _a;
    return (_a = this.config.apis) != null ? _a : [];
  }
  getFeatureFlags() {
    var _a, _b;
    return (_b = (_a = this.config.featureFlags) == null ? void 0 : _a.slice()) != null ? _b : [];
  }
  get routes() {
    var _a;
    return (_a = this.config.routes) != null ? _a : {};
  }
  get externalRoutes() {
    var _a;
    return (_a = this.config.externalRoutes) != null ? _a : {};
  }
  provide(extension) {
    return extension.expose(this);
  }
  toString() {
    return `plugin{${this.config.id}}`;
  }
}
function createPlugin(config) {
  return new PluginImpl(config);
}

export { AnalyticsContext, attachComponentData, createApiFactory, createComponentExtension, createExternalRouteRef, createPlugin, createReactExtension, createRoutableExtension, createRouteRef, createSubRouteRef, getComponentData, useAnalytics, useApp, useElementFilter, useRouteRef, useRouteRefParams };
//# sourceMappingURL=index.esm.js.map
